import { lazy, Suspense, useCallback, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { isUserAuthorized, getCurrentTheme } from "./store/selectors";
import {
  checkToken,
  fetchUserProfile,
  loadInitialData,
} from "./actions/authActions";
import { Loading } from "./layout/utils/AppLoading";
import withClearCache from "./ClearCache";
import { ConfigProvider, theme } from "antd";
import es_ES from "antd/es/locale/es_ES";
import { SET_CURRENT_ROUTE, THEME } from "./constants";
import { getCookie } from "./utils";
import history from "./history";
import {
  clearAuthReturnPath,
  getAuthReturnPath,
  getRouteKeyFromPath,
  saveAuthReturnPath,
} from "./utils/authReturnPath";

const MainContainer = lazy(() => import("./containers/MainContainer"));
const AuthContainer = lazy(() => import("./containers/AuthContainer"));

const PUBLIC_FOLLOW_UP_PATH = /^\/incidences\/seguimiento\/[a-f0-9]{24}$/i;

const App = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const currentTheme = useSelector(getCurrentTheme);

  const { auth } = useSelector(isUserAuthorized) || {};
  const isPublicFollowUpForm = PUBLIC_FOLLOW_UP_PATH.test(location.pathname);
  const useLightTheme = isPublicFollowUpForm ? true : currentTheme;
  const pendingReturnRedirect = useRef(false);

  const ClearCacheComponent = withClearCache(() => {
    return <></>;
  });

  const fetchInitialData = useCallback(async () => {
    console.log("Fetching initial data")
    dispatch(fetchUserProfile());
    dispatch(loadInitialData());
    console.log("Finish fetching initial data")
  }, [dispatch]);

  const checkPersistency = useCallback(() => {
    dispatch(checkToken());
  }, [dispatch]);

  useEffect(() => {
    auth ? fetchInitialData() : checkPersistency();
  }, [auth, fetchInitialData, checkPersistency]);

  useEffect(() => {
    if (auth) {
      return;
    }
    saveAuthReturnPath(location.pathname, location.search);
  }, [auth, location.pathname, location.search]);

  useEffect(() => {
    if (!auth || pendingReturnRedirect.current) {
      return;
    }
    const returnPath = getAuthReturnPath();
    if (!returnPath) {
      return;
    }
    clearAuthReturnPath();
    pendingReturnRedirect.current = true;
    dispatch({
      type: SET_CURRENT_ROUTE,
      payload: getRouteKeyFromPath(returnPath),
    });
    history.push(returnPath);
  }, [auth, dispatch]);

  useEffect(() => {
    if (!auth) {
      pendingReturnRedirect.current = false;
    }
  }, [auth]);

  useEffect(() => {
    const refreshTheme = async () => {
      await dispatch({ type: THEME, payload: await getCookie(THEME) })
    }
    refreshTheme()
  }, [dispatch])

  return (
    <Suspense fallback={<Loading centered />}>
      <ConfigProvider
        locale={es_ES}
        theme={{
          token: {
            borderRadius: 5,
            colorPrimary: "#27B4CC",
            fontFamily: "Charlie Display",
            colorBgBase: useLightTheme ? "#eee" : "#222",
            colorTextDisabled: useLightTheme ? "rgba(0, 0, 0, 0.50)" : "rgba(255, 255, 255, 0.50)",
          },
          components: {
            Switch: {
              colorPrimary: '#27B4CC',
              colorPrimaryHover: "#27B4CC",
            }
          },
          algorithm: useLightTheme ? theme.defaultAlgorithm : theme.darkAlgorithm,
        }}
      >
        <ClearCacheComponent />
        {auth ? <MainContainer /> : <AuthContainer />}
      </ConfigProvider>
    </Suspense>
  );
};

export default App;
